<template>
    <div v-if="activeExportInvoice" class="pa-5 pt-2 compact-form">
        <v-row no-gutters class="flex-row">
            <v-col class="">
                <v-col class="mt-2 row-title pa-0">{{ "Invoice date" | t }}</v-col>
                <v-col class="pa-0">
                    <date-picker v-model="invoiceDate" :popover="{ placement: 'bottom-start', visibility: 'click' }">
                        <v-text-field
                            :value="getDate(invoiceDate)"
                            append-icon="fas fa-calendar"
                            class="text-field-item mb-1 t-spacing"
                            dense
                            hide-details
                            outlined
                        />
                    </date-picker>
                </v-col>
            </v-col>
            <v-col class="ml-2">
                <v-col class="mt-2 row-title pa-0">{{ "Arrival date" | t }}</v-col>
                <v-col class="pa-0">
                    <date-picker v-model="arrivalDate" :popover="{ placement: 'bottom', visibility: 'click' }">
                        <v-text-field
                            :value="getDate(arrivalDate)"
                            append-icon="fas fa-calendar"
                            class="text-field-item mb-1 t-spacing"
                            dense
                            hide-details
                            outlined
                        />
                    </date-picker>
                </v-col>
            </v-col>
            <v-col class="ml-2">
                <v-col class="mt-2 row-title pa-0">{{ "Payroll date" | t }}</v-col>
                <v-col class="pa-0">
                    <vue-monthly-picker
                        v-model="payrollDate"
                        dateFormat="MMMM YYYY"
                        :monthLabels="months"
                        :clearOption="false"
                        lang="et"
                        inputClass="payroll-date"
                    />
                </v-col>
            </v-col>
            <v-col class="ml-2">
                <v-col class="mt-2 row-title pa-0">{{ "Approved" | t }}</v-col>
                <v-col class="pa-0">
                    <v-simple-checkbox
                        v-model="activeExportInvoice.is_active"
                        @input="updateField($event, 'is_active')"
                    />
                </v-col>
            </v-col>
        </v-row>
        <v-divider class="mt-3 mb-3" />
        <v-row no-gutters>
            <v-col>
                <v-col class="row-title col-item">{{ "Total received" | t }}</v-col>
                <v-col class="col-item">
                    <v-text-field
                        v-model="localTotalArrived"
                        class="text-field-item t-spacing mb-0"
                        dense
                        prepend-inner-icon="euro"
                        hide-details
                        outlined
                        type="number"
                        step="100"
                        @input="debouncedUpdateField($event, 'total_arrived')"
                        @keypress="onlyIntegersWithComa"
                    />
                </v-col>
            </v-col>
            <v-col class="ml-1">
                <v-col class="row-title col-item">{{ "Percent" | t }}</v-col>
                <v-col class="col-item">
                    <v-text-field
                        v-model="localPercent"
                        class="text-field-item t-spacing mb-0"
                        dense
                        hide-details
                        outlined
                        type="number"
                        @input="debouncedUpdateField($event, 'percent')"
                        @keypress="onlyIntegersWithComa"
                    />
                </v-col>
            </v-col>
            <v-col class="ml-1">
                <v-col class="row-title col-item">{{ "Sum" | t }}</v-col>
                <v-col class="pa-0 pt-1 pb-4">
                    {{ activeExportInvoice.total_net ? rn(activeExportInvoice.total_net) : "-" }}
                </v-col>
            </v-col>
            <v-col>
                <v-col class="row-title col-item ml-1">{{ "Profit" | t }}</v-col>
                <v-col class="pl-1 pt-1 pb-4">
                    {{ activeExportInvoice.total_profit ? rn(activeExportInvoice.total_profit) : "-" }}
                </v-col>
            </v-col>
        </v-row>
        <v-divider class="mt-0 mb-3" />
        <v-row no-gutters>
            <v-col>
                <v-combobox
                    v-model="client"
                    :items="export_clients"
                    :label="'Client' | t"
                    :loading="isLoading"
                    :placeholder="'Client' | t"
                    class="t-spacing"
                    clearable
                    dense
                    hide-details
                    hide-no-data
                    hide-selected
                    item-text="name"
                    item-value="id"
                    outlined
                    @change="saveClientInfo"
                />
            </v-col>
            <v-col class="ml-1">
                <v-autocomplete
                    v-model="manager"
                    :items="users"
                    :label="'Manager' | t"
                    :loading="isLoading"
                    :placeholder="'Manager' | t"
                    class="t-spacing"
                    clearable
                    dense
                    hide-details
                    hide-no-data
                    hide-selected
                    item-text="name"
                    item-value="id"
                    outlined
                    return-object
                    @change="saveManagerInfo"
                />
            </v-col>
        </v-row>
        <v-divider class="t-divider my-2" />
        <rows-edit-tab />
        <v-divider class="t-divider my-2" />
        <v-row no-gutters>
            <v-col class="ml-1">
                <v-col class="row-title col-item">{{ "Link" | t }}</v-col>
                <v-col class="col-item">
                    <v-text-field
                        v-model="localOdooLink"
                        class="text-field-item t-spacing mb-0"
                        dense
                        hide-details
                        outlined
                        type="text"
                        @input="debouncedUpdateField($event, 'odoo_link')"
                    />
                </v-col>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import Store from "@/store";
import { mapActions, mapState } from "vuex";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import VueMonthlyPicker from "vue-monthly-picker";
import moment from "moment";
import RowsEditTab from "@/pages/export-sale-invoice/export-sale-invoice-tab-items/rows-edit-tab.vue";

// Simple debounce function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default {
    name: "general-info-edit-tab",
    components: {
        DatePicker,
        VueMonthlyPicker,
        RowsEditTab
    },
    data() {
        return {
            invoiceDate: "",
            arrivalDate: "",
            payrollDate: "",
            isLoading: false,
            manager: null,
            client: null,
            months: [
                this.t("January"),
                this.t("February"),
                this.t("March"),
                this.t("April"),
                this.t("May"),
                this.t("June"),
                this.t("July"),
                this.t("August"),
                this.t("September"),
                this.t("October"),
                this.t("November"),
                this.t("December")
            ],
            // Local values for debounced fields
            localTotalArrived: null,
            localPercent: null,
            localOdooLink: null
        };
    },
    async beforeMount() {
        await this.getFrequentlyUsedQueries({ users: true, export_clients: true });
    },
    created() {
        // Create debounced version of updateField - waits 300ms after last input
        this.debouncedUpdateField = debounce((value, field) => {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: field,
                value: value
            });
        }, 300);
    },
    async mounted() {
        if (this.activeExportInvoice) setTimeout(this.setData, 500);
        else setTimeout(this.setData, 1500);

        // Initialize local values from store
        if (this.activeExportInvoice) {
            this.localTotalArrived = this.activeExportInvoice.total_arrived;
            this.localPercent = this.activeExportInvoice.percent;
            this.localOdooLink = this.activeExportInvoice.odoo_link;
        }
    },
    watch: {
        invoiceDate: function(value) {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "invoice_date",
                value: moment(value).format("DD.MM.YYYY")
            });
            if (!this.arrivalDate && !this.activeExportInvoice.arrival_date) this.arrivalDate = value;
            if (!this.payrollDate && !this.activeExportInvoice.payroll_date) this.payrollDate = moment(value);
        },
        arrivalDate: function(value) {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "arrival_date",
                value: moment(value).format("DD.MM.YYYY")
            });
        },
        payrollDate: function(value) {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "payroll_date",
                value: moment(value).format("DD.MM.YYYY")
            });
        },
        // Sync local values when store changes externally
        "activeExportInvoice.total_arrived": function(newVal) {
            if (newVal !== this.localTotalArrived) {
                this.localTotalArrived = newVal;
            }
        },
        "activeExportInvoice.percent": function(newVal) {
            if (newVal !== this.localPercent) {
                this.localPercent = newVal;
            }
        },
        "activeExportInvoice.odoo_link": function(newVal) {
            if (newVal !== this.localOdooLink) {
                this.localOdooLink = newVal;
            }
        }
    },
    methods: {
        ...mapActions(["getFrequentlyUsedQueries"]),
        updateField(value, field) {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", { prop: field, value: value });
        },
        saveManagerInfo() {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "manager_id",
                value: this.manager ? this.manager.id : null
            });
        },
        saveClientInfo() {
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "client_name",
                value: this.client.name ? this.client.name : this.client
            });
        },
        async setData() {
            if (this.export_clients.length <= 0) {
                await this.getFrequentlyUsedQueries({ users: true, export_clients: true, products: true });
            }
            if (this.$route.params.exportInvoiceId !== "new") {
                this.manager = await this.users.find(x => x.id === this.activeExportInvoice.manager_id);
                this.client = await this.export_clients.find(x => x.id === this.activeExportInvoice.client_id);
                if (this.activeExportInvoice.invoice_date)
                    this.invoiceDate = await moment(this.activeExportInvoice.invoice_date, "DD.MM.YYYY").toDate();
                if (this.activeExportInvoice.arrival_date)
                    this.arrivalDate = await moment(this.activeExportInvoice.arrival_date, "DD.MM.YYYY").toDate();
                if (this.activeExportInvoice.payroll_date)
                    this.payrollDate = await moment(this.activeExportInvoice.payroll_date, "DD.MM.YYYY");
            }
        },
        getDate(value) {
            if (value) return moment(value).format("DD.MM.YYYY");
            else return null;
        },
        getPayrollDate(value) {
            if (value)
                return (
                    this.t(moment(value, "DD.MM.YYYY").format("MMMM")) +
                    " " +
                    moment(value, "DD.MM.YYYY").format("YYYY")
                );
            else return "-";
        },
        onlyIntegersWithComa($event) {
            let keyCode = $event.keyCode ? $event.keyCode : $event.key;

            if (keyCode < 44 || keyCode === 47 || keyCode === 45 || keyCode > 57) {
                $event.preventDefault();
            }
        }
    },
    computed: {
        ...mapState(["export_clients", "users"]),
        ...mapState("ExportSaleInvoiceStore", ["activeExportInvoice"])
    }
};
</script>

<style scoped>
.row-title {
    color: #888;
}
.col-item {
    padding: 0 !important;
}
::v-deep .t-spacing > .v-input__control > .v-input__slot {
    min-height: 9px !important;
}
::v-deep .t-spacing .v-input__icon {
    padding-bottom: 8px;
}
::v-deep button.fa-times-circle {
    font-size: 14px;
}
::v-deep .payroll-date {
    border: 1px solid rgba(96, 96, 96, 0.32) !important;
    border-radius: 6px !important;
    padding: 2px !important;
    padding-left: 10px !important;
    font-size: 16px !important;
    height: 30px;
}
::v-deep .vue-monthly-picker .vmp-input-append {
    padding: 3px !important;
}
::v-deep .flexbox {
    color: black;
    font-weight: bolder;
}
.t-divider {
    border-color: darkgrey !important;
}
</style>
